import { shouldFlagCardOverflow } from "../../Cards/cardOverflowUtils";
import { layoutCreditBox } from "./scene";

// Project.jsx sizes the card editor frame from this height.
export const CARD_PREVIEW_HEIGHT = 450;
// CardPreview side-by-side row: `gap-x-[30px]`.
export const CARD_SIDE_BY_SIDE_GAP = 30;

export function cardFrameSize(resolution, frameHeight = CARD_PREVIEW_HEIGHT) {
  const videoWidth = Number(resolution?.videoWidth);
  const videoHeight = Number(resolution?.videoHeight);
  const aspect =
    videoWidth > 0 && videoHeight > 0 ? videoWidth / videoHeight : 16 / 9;
  return {
    width: Math.round(frameHeight * aspect),
    height: frameHeight,
  };
}

const shiftBy = (item, dy) => {
  if (!dy) return item;
  const next = { ...item };
  if (typeof next.y === "number") next.y += dy;
  if (typeof next.top === "number") next.top += dy;
  if (typeof next.bottom === "number") next.bottom += dy;
  return next;
};

/**
 * Move a content box down so it sits in the middle of the frame. A box
 * taller than the frame stays top-aligned and is marked overflowing.
 * The hit target is the full frame height of the column.
 */
const placeInFrame = (box, frameHeight) => {
  const contentHeight = box.height;
  const overflow = contentHeight > frameHeight;
  const dy = overflow ? 0 : (frameHeight - contentHeight) / 2;
  const shifted = shiftBy(box, dy);
  return {
    ...shifted,
    ops: box.ops.map((op) => shiftBy(op, dy)),
    hits: box.hits.map((hit) => shiftBy(hit, dy)),
    top: 0,
    height: frameHeight,
    contentTop: box.top + dy,
    contentHeight,
    overflow,
    focus: { x: box.x, y: 0, width: box.width, height: frameHeight },
  };
};

/**
 * One fixed aspect frame per card. A side-by-side credit and the following
 * credit share a frame; each column is centered on its own.
 *
 * env: { measurer, fontStyle, settings, images, resolution, width, frameHeight }
 */
export function layoutCardScene({
  credits = [],
  settings,
  fontStyle,
  measurer,
  images,
  resolution,
  width,
  frameHeight = CARD_PREVIEW_HEIGHT,
}) {
  const frame = cardFrameSize(resolution, frameHeight);
  const frameWidth = width ?? frame.width;
  const height = frameHeight;
  const env = {
    measurer,
    fontStyle: fontStyle || {},
    settings: settings || {},
    images,
    isCard: true,
  };
  const wrappers = [];
  const overflowingIndexes = [];

  const placeColumn = (credit, idx, region) => {
    const box = placeInFrame(
      layoutCreditBox(credit, idx, region, env),
      height,
    );
    if (
      shouldFlagCardOverflow(
        { block_type: credit?.options?.block_type },
        box.contentHeight,
        height,
      )
    ) {
      overflowingIndexes.push(idx);
    }
    return box;
  };

  (credits || []).forEach((credit, idx) => {
    const inset = credit?.options?.block_inset;
    if (inset === "sidebyside") {
      const columnWidth = (frameWidth - CARD_SIDE_BY_SIDE_GAP) / 2;
      const boxes = [
        placeColumn(credit, idx, { left: 0, width: columnWidth, top: 0 }),
      ];
      if (credits[idx + 1]) {
        boxes.push(
          placeColumn(credits[idx + 1], idx + 1, {
            left: columnWidth + CARD_SIDE_BY_SIDE_GAP,
            width: columnWidth,
            top: 0,
          }),
        );
      }
      wrappers.push({ idx, top: 0, height, credits: boxes });
      return;
    }
    if (credits[idx - 1]?.options?.block_inset === "sidebyside") return;

    wrappers.push({
      idx,
      top: 0,
      height,
      credits: [
        placeColumn(credit, idx, { left: 0, width: frameWidth, top: 0 }),
      ],
    });
  });

  return {
    width: frameWidth,
    frameHeight: height,
    height,
    blankHeight: 0,
    contentTop: 0,
    contentBottom: height,
    wrappers,
    overflowingIndexes,
  };
}

/** Same shape as collectCardOverflowBlocks, from layout instead of DOM. */
export function cardSceneOverflowMap(scene, blocks = []) {
  const overflowing = new Map();
  (scene?.overflowingIndexes || []).forEach((index) => {
    overflowing.set(index, {
      index,
      name: blocks[index]?.block_name || `Block ${index + 1}`,
    });
  });
  return overflowing;
}
